/**
 * @file Gear device-action vocabulary (CLIENT mirror).
 *
 * The eight device actions an admin can submit into the pending-Abra
 * queue, plus their human labels. This is the single source of truth for
 * the verb set and the verb→label map — previously both were hand-copied
 * across functions/gear-actions.js (ACTION_TYPES + actionLabel),
 * src/pages/device-detail.js (pendingActionLabel), and
 * src/pages/pending.js (ACTION_LABELS), which had already drifted (the
 * `loot` label was missing from device-detail, rendering the raw string).
 *
 * Mirrored verbatim in functions/lib/gear-actions.js. The two files MUST
 * stay byte-aligned (same keys, same values) — tests/invariants.test.js
 * asserts it.
 */

/**
 * Ordered list of valid action verbs. Order drives any UI that iterates.
 *
 * `register` is the odd one out: every other verb acts on a device Abra
 * already has, so the row carries a deviceId and reconciles against that
 * doc. A register row is a device Abra DOESN'T have yet (brand-new, or
 * MDM-only) handed to someone — Finance creates the asset and assigns it,
 * and the row reconciles by SERIAL once the Flexi record lands. Same Slack
 * wording as a hand-over (Finance sees one message shape).
 */
export const GEAR_ACTIONS = Object.freeze([
  'register',
  'handover',
  'takeback',
  'transfer',
  'make_testing',
  'sell',
  'loot',
  'dispose',
]);

export const GEAR_ACTION_SET = new Set(GEAR_ACTIONS);

/** Canonical verb → human label. One label per verb, used everywhere. */
export const GEAR_ACTION_LABELS = Object.freeze({
  register:     'New device',
  handover:     'Hand over',
  takeback:     'Take back',
  transfer:     'Transfer',
  make_testing: 'Make into test device',
  sell:         'Sell',
  loot:         'Loot sale',
  dispose:      'Dispose',
});

/** Label lookup with a safe fallback to the raw verb. */
export function gearActionLabel(action) {
  return GEAR_ACTION_LABELS[action] || action;
}
